// RealTimeTranscription.js
import React, { useCallback, useEffect, useRef } from 'react';
import SpeechRecognition, {
    useSpeechRecognition,
} from 'react-speech-recognition';
import { Button } from '@quyva/shared-ui';
import { francAll } from 'franc';
import { supportedLanguages } from '@quyva/common';
import {
    faDeleteLeft,
    faMicrophone,
    faMicrophoneSlash,
} from '@fortawesome/free-solid-svg-icons';


// franc returns ISO-639-3 codes; Web Speech works best with BCP-47 locales.
// We map common ISO-639-3 codes to reasonable locales, and fall back to ISO-639-1
// language tags (e.g. 'fr', 'de') when a full locale isn't known.
const iso6393ToLocale = {
    // align with @quyva/common supportedLanguages
    eng: supportedLanguages.ENGLISH_US,
    hin: supportedLanguages['हिन्दी'],
    mar: supportedLanguages['मराठी'],

    // common additional languages
    spa: 'es-ES',
    fra: 'fr-FR',
    deu: 'de-DE',
    ita: 'it-IT',
    por: 'pt-PT',
    rus: 'ru-RU',
    jpn: 'ja-JP',
    kor: 'ko-KR',
    zho: 'zh-CN',
    ara: 'ar-SA',
};

// Minimal ISO-639-3 -> ISO-639-1 mapping for fallback.
// If you want more accuracy for rare languages, extend this map.
const iso6393ToIso6391 = {
    eng: 'en',
    hin: 'hi',
    mar: 'mr',
    spa: 'es',
    fra: 'fr',
    deu: 'de',
    ita: 'it',
    por: 'pt',
    rus: 'ru',
    jpn: 'ja',
    kor: 'ko',
    zho: 'zh',
    ara: 'ar',
};

const toBrowserLocale = (iso6393) => {
    const direct = iso6393ToLocale[iso6393];
    if (direct) return direct;

    const iso1 = iso6393ToIso6391[iso6393];
    if (!iso1) return null;

    // Prefer an installed browser locale if it matches the language.
    const navLang = (navigator?.language || '').toLowerCase();
    if (navLang && navLang.startsWith(`${iso1}-`)) {
        return navigator.language;
    }

    // Use plain ISO-639-1 language tag as a valid BCP-47 fallback.
    return iso1;
};

const detectLocaleFromText = (text) => {
    if (!text || text.trim().length < 10) return null;

    try {
        const codes = francAll(text, { minLength: 10 });
        const top = Array.isArray(codes) && codes.length ? codes[0] : null;
        const langCode = Array.isArray(top) ? top[0] : top;
        if (!langCode || langCode === 'und') return null;

        return toBrowserLocale(langCode) || null;
    } catch (e) {
        return null;
    }
};

export const RealTimeTranscription = ({
    onTranscription,
    onTranscriptionReset,
    isListening,
    setIsListening,
    activeAnswer,
    enableSpeaker,
    setEnableSpeaker,
    setActiveAudioId,
    isSmallScreen,
}) => {
    const { transcript, resetTranscript, listening } = useSpeechRecognition();
    const lastDetectedLangRef = useRef(null);
    const lastDetectedLangCountRef = useRef(0);

    const detectAndMaybeSwitchLanguage = useCallback(
        (text) => {
            if (!isListening) return;
            const locale = detectLocaleFromText(text);
            if (!locale) return;

            // small hysteresis: require the same detected language twice
            if (lastDetectedLangRef.current === locale) {
                lastDetectedLangCountRef.current += 1;
            } else {
                lastDetectedLangRef.current = locale;
                lastDetectedLangCountRef.current = 1;
            }

            if (lastDetectedLangCountRef.current < 2) return;
            lastDetectedLangCountRef.current = 0;

            // restart recognition with new language
            SpeechRecognition.stopListening();
            SpeechRecognition.startListening({
                continuous: true,
                language: locale,
            });
        },
        [isListening]
    );

    // Sync transcript with liveTranscript state in real-time + auto detect language
    useEffect(() => {
        if (transcript && onTranscription) {
            onTranscription(transcript);
            detectAndMaybeSwitchLanguage(transcript);
        }
    }, [transcript, onTranscription, detectAndMaybeSwitchLanguage]);

    useEffect(() => {
        if (isListening) {
            handleStart();
        } else {
            handleStop();
        }
    }, [isListening]);

    useEffect(() => {
        resetTranscript();
    }, [activeAnswer]);

    // Start recording and listening
    const handleStart = () => {
        if (!isListening) {
            setIsListening(true);
            setActiveAudioId(null);
        }

        // reset detection state for a fresh session
        lastDetectedLangRef.current = null;
        lastDetectedLangCountRef.current = 0;

        // Start without forcing a language; browser will use its default.
        // We will optionally switch once we detect a stable language from transcript.
        SpeechRecognition.startListening({
            continuous: true,
        });

        setTimeout(() => {
            setEnableSpeaker(true);
        }, 500);
    };

    // Stop recording and stop listening
    const handleStop = () => {
        if (isListening) {
            setIsListening(false);
        }

        SpeechRecognition.stopListening();
        onTranscription(transcript);
    };

    // Reset the transcript (optional)
    const handleReset = () => {
        resetTranscript();
        if (onTranscriptionReset) {
            onTranscriptionReset();
        }
    };

    return (
        <div className='flex items-center'>
            {!isListening && (
                <div className='flex items-center'>
                    <Button
                        text={null}
                        className={``}
                        hoverText='Unmute mic to start listening to your query'
                        disabled={isListening}
                        onClick={handleStart}
                        icon={faMicrophoneSlash}
                        type={'success'}
                    />
                </div>
            )}
            {isListening && (
                <div className='flex border rounded-full'>
                    <Button
                        text={null}
                        className={`mr-1`}
                        hoverText='Clear your question input'
                        disabled={!isListening}
                        onClick={handleReset}
                        icon={faDeleteLeft}
                        type={'danger'}
                    />

                    <Button
                        text={null}
                        className={`ml-1 animate-pulse`}
                        hoverText='Mute mic to stop Listening to your query'
                        disabled={!isListening}
                        onClick={handleStop}
                        icon={faMicrophone}
                        type={'danger'}
                    />
                </div>
            )}
        </div>
    );
};
